/**
 * BlockRenderer — maps block_type to component and renders.
 *
 * injectedProps allows callers to pass extra props (siteData, fetchProducts,
 * onSubmit, editMode, onSelect) to every block without each block needing to
 * import context directly. This is what makes the shared package work in both
 * the public ecommerce site and the back-office WYSIWYG editor.
 */
import React from 'react';
import HeroBlock          from './blocks/HeroBlock';
import TextBlock          from './blocks/TextBlock';
import TwoColumnBlock     from './blocks/TwoColumnBlock';
import FeatureCardsBlock  from './blocks/FeatureCardsBlock';
import GalleryBlock       from './blocks/GalleryBlock';
import CTABlock           from './blocks/CTABlock';
import FAQBlock           from './blocks/FAQBlock';
import TestimonialBlock   from './blocks/TestimonialBlock';
import ImageBlock         from './blocks/ImageBlock';
import SpacerBlock        from './blocks/SpacerBlock';
import VideoBlock         from './blocks/VideoBlock';
import HTMLBlock          from './blocks/HTMLBlock';
import ContactInfoBlock   from './blocks/ContactInfoBlock';
import ContactFormBlock   from './blocks/ContactFormBlock';
import ProductGridBlock   from './blocks/ProductGridBlock';
import EventsListBlock    from './blocks/EventsListBlock';

export const BLOCK_COMPONENTS = {
  'hero':                  HeroBlock,
  'text':                  TextBlock,
  'two-column':            TwoColumnBlock,
  'feature-cards':         FeatureCardsBlock,
  'features':              FeatureCardsBlock,
  'gallery':               GalleryBlock,
  'cta':                   CTABlock,
  'faq':                   FAQBlock,
  'testimonial':           TestimonialBlock,
  'testimonials':          TestimonialBlock,
  'testimonials-carousel': TestimonialBlock,
  'image':                 ImageBlock,
  'spacer':                SpacerBlock,
  'video':                 VideoBlock,
  'html':                  HTMLBlock,
  'custom-html':           HTMLBlock,
  'contact-info':          ContactInfoBlock,
  'contact-form':          ContactFormBlock,
  'form':                  ContactFormBlock,
  'product-grid':          ProductGridBlock,
  'products':              ProductGridBlock,
  'events':                EventsListBlock,
  'market-events':         EventsListBlock,
};

/**
 * renderBlock — render a single block with optional injected props.
 */
export const renderBlock = (block, index = 0, injectedProps = {}) => {
  const Component = BLOCK_COMPONENTS[block.block_type];
  if (!Component) {
    console.warn(`@busmgr/blocks: unknown block type "${block.block_type}"`);
    return null;
  }
  return (
    <Component
      key={block.id || `block-${index}`}
      content={block.content || {}}
      settings={block.settings || {}}
      blockType={block.block_type}
      {...injectedProps}
    />
  );
};

/**
 * BlockRenderer component — renders an ordered array of blocks.
 */
const BlockRenderer = ({ blocks = [], injectedProps = {} }) => {
  if (!blocks?.length) return null;
  return (
    <div className="block-renderer">
      {[...blocks]
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map((block, i) => renderBlock(block, i, injectedProps))
      }
    </div>
  );
};

export default BlockRenderer;
